import type { FC, MouseEvent, ReactElement } from 'react';
import classnames from 'classnames';
import * as _ from 'lodash';
import type { ButtonSize, ButtonType } from './button-constants';
import { BUTTON_SIZE, BUTTON_TYPE } from './button-constants';

export type ButtonProps = {
  type: 'button' | 'submit';
  className: string;
  disabled: boolean;
  title?: string;
  onClick: (event: MouseEvent<HTMLElement>) => void;
  /* eslint-disable @typescript-eslint/naming-convention */
  'aria-label'?: string;
  'data-test-id'?: string;
  /* eslint-enable @typescript-eslint/naming-convention */
};

export type GetButtonProps = (additionalProps?: Record<string, any>) => ButtonProps;

export type ButtonBaseProps = {
  type?: ButtonType;
  size?: ButtonSize;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  isSubmitButton?: boolean;
  title?: string;
  testId?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  children: (getButtonProps: GetButtonProps) => ReactElement;
};

type ButtonClassNamesProps = Pick<ButtonBaseProps, 'type' | 'size' | 'isDisabled' | 'isFullWidth'>;

//  Function for encapsulating classnames for Button.
export const getButtonClassNames = ({ type, size, isFullWidth, isDisabled }: ButtonClassNamesProps): string =>
  classnames('fossa-button', {
    'fossa-button--type-primary': type === BUTTON_TYPE.PRIMARY,
    'fossa-button--type-secondary': type === BUTTON_TYPE.SECONDARY,
    'fossa-button--type-slate': type === BUTTON_TYPE.SLATE,
    'fossa-button--type-outline': type === BUTTON_TYPE.OUTLINE,
    'fossa-button--type-outline-light': type === BUTTON_TYPE.OUTLINE_LIGHT,
    'fossa-button--type-error': type === BUTTON_TYPE.ERROR,
    'fossa-button--type-dark': type === BUTTON_TYPE.DARK,
    'fossa-button--type-anchor': type === BUTTON_TYPE.ANCHOR,
    'fossa-button--type-text': type === BUTTON_TYPE.TEXT,
    'fossa-button--size-icon': size === BUTTON_SIZE.ICON,
    'fossa-button--size-micro': size === BUTTON_SIZE.MICRO,
    'fossa-button--size-small': size === BUTTON_SIZE.SMALL,
    'fossa-button--size-medium': size === BUTTON_SIZE.MEDIUM,
    'fossa-button--size-large': size === BUTTON_SIZE.LARGE,
    'fossa-button--size-xlarge': size === BUTTON_SIZE.XLARGE,
    'fossa-button--full-width': isFullWidth,
    'fossa-button--disabled': isDisabled,
  });

/**
 * Base component for Button, following the render-props/child-as-a-function pattern.
 * This allows buttons to be more customizable than just using <Button /> directly.
 *
 * @param {Function} children - Function which recieves getButtonProps and returns custom button implementation.
 * @example
 * import { ButtonBase } from '../generic/Button';
 *
 * const MyComponent = () => (
 *   <ButtonBase onClick={() => console.log('clicked')}>
 *     {(getButtonProps) => (
 *        <button
 *          {...getButtonProps({
 *            style: {
 *              // Add custom style overrides here
 *            }
 *          })}
 *        >
 *          Custom Button
 *        </button>
 *     )}
 *   </ButtonBase>
 * );
 */
const ButtonBase: FC<ButtonBaseProps> = ({
  children,
  type = BUTTON_TYPE.PRIMARY,
  size = BUTTON_SIZE.SMALL,
  isDisabled,
  onClick = _.noop,
  title,
  isFullWidth,
  isSubmitButton,
  testId,
}: ButtonBaseProps) => {
  const getButtonProps: GetButtonProps = (additionalProps: Record<string, any> = {}): ButtonProps => ({
    type: isSubmitButton ? 'submit' : 'button',
    className: getButtonClassNames({
      type,
      size,
      isDisabled,
      isFullWidth,
    }),
    disabled: !!isDisabled,
    onClick: isDisabled ? _.noop : onClick,
    title,
    'aria-label': title,
    'data-test-id': testId,
    ...additionalProps,
  });
  return <>{children(getButtonProps)}</>;
};

export default ButtonBase;
